import React, { useState, useEffect } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  IconButton,
  Box,
  Typography,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  Chip,
  AppBar,
  Toolbar
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { styled } from "@mui/material/styles";
import axios from "axios";
import { useLoader } from "../../../context/LoaderProvider";

// Styled Components
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  boxShadow: "none",
  border: "1px solid #e0e0e0",
  borderRadius: "8px",
  maxHeight: "calc(100vh - 140px)", 
 // Adjust based on header height
  overflow: "auto",
  "& .MuiTableHead-root": {
    position: "sticky",
    top: 0,
    zIndex: 2,
    backgroundColor: "#fff"
  }
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: "12px 16px",
  fontSize: "14px",
  color: "#333",
  borderBottom: "1px solid #e0e0e0"
}));

const StyledTableHeadCell = styled(StyledTableCell)(({ theme }) => ({
  fontWeight: 600,
  backgroundColor: "#f8f9fa",
  color: "#495057",
  position: "sticky",
  top: 0,
  zIndex: 1
}));

const SearchField = styled(TextField)(({ theme }) => ({
  width: "300px",
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "#fff",
    "& fieldset": {
      borderColor: "#e0e0e0"
    },
    "&:hover fieldset": {
      borderColor: "#e66d33"
    },
    "&.Mui-focused fieldset": {
      borderColor: "#e66d33"
    }
  }
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: "12px",
    width: "450px"
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  backgroundColor: "#f8f9fa",
  padding: "16px 24px",
  borderBottom: "1px solid #e0e0e0",
  "& h6": {
    fontSize: "18px",
    fontWeight: 600,
    color: "#495057"
  }
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: "24px"
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: "16px 24px",
  borderTop: "1px solid #e0e0e0"
}));

const FixedHeader = styled(Box)(({ theme }) => ({
  position: "sticky",
  top: 0,
  zIndex: 100,
  backgroundColor: "#f8f9fa",
  padding: "16px 0",
  marginTop: "66px",
  marginBottom: "16px",
  
  boxShadow: "0 2px 8px rgba(0,0,0,0.06)" // 🔥 premium
}));
// 20 Dummy employees data
const dummyEmployees = [
  {
    vendorId: 1001,
    name: "John Smith",
    email: "john.smith@example.com",
    mobileNumber: "+1 234-567-8901"
  },
  {
    vendorId: 1002,
    name: "Sarah Johnson",
    email: "sarah.j@example.com",
    mobileNumber: "+1 345-678-9012"
  },
  {
    vendorId: 1003,
    name: "Michael Brown",
    email: "michael.b@example.com",
    mobileNumber: "+1 456-789-0123"
  },
  {
    vendorId: 1004,
    name: "Emily Davis",
    email: "emily.d@example.com",
    mobileNumber: "+1 567-890-1234"
  },
  {
    vendorId: 1005,
    name: "David Wilson",
    email: "david.w@example.com",
    mobileNumber: "+1 678-901-2345"
  },
  {
    vendorId: 1006,
    name: "Lisa Anderson",
    email: "lisa.a@example.com",
    mobileNumber: "+1 789-012-3456"
  },
  {
    vendorId: 1007,
    name: "Robert Taylor",
    email: "robert.t@example.com",
    mobileNumber: "+1 890-123-4567"
  },
  {
    vendorId: 1008,
    name: "Jennifer Martinez",
    email: "jennifer.m@example.com",
    mobileNumber: "+1 901-234-5678"
  },
  {
    vendorId: 1009,
    name: "William Lee",
    email: "william.l@example.com",
    mobileNumber: "+1 012-345-6789"
  },
  {
    vendorId: 1010,
    name: "Maria Garcia",
    email: "maria.g@example.com",
    mobileNumber: "+1 123-456-7890"
  },
  {
    vendorId: 1011,
    name: "James Brown",
    email: "james.b@example.com",
    mobileNumber: "+1 234-567-8901"
  },
  {
    vendorId: 1012,
    name: "Patricia Miller",
    email: "patricia.m@example.com",
    mobileNumber: "+1 345-678-9012"
  },
  {
    vendorId: 1013,
    name: "Richard Davis",
    email: "richard.d@example.com",
    mobileNumber: "+1 456-789-0123"
  },
  {
    vendorId: 1014,
    name: "Elizabeth Rodriguez",
    email: "elizabeth.r@example.com",
    mobileNumber: "+1 567-890-1234"
  },
  {
    vendorId: 1015,
    name: "Charles Wilson",
    email: "charles.w@example.com",
    mobileNumber: "+1 678-901-2345"
  },
  {
    vendorId: 1016,
    name: "Susan Moore",
    email: "susan.m@example.com",
    mobileNumber: "+1 789-012-3456"
  },
  {
    vendorId: 1017,
    name: "Joseph Taylor",
    email: "joseph.t@example.com",
    mobileNumber: "+1 890-123-4567"
  },
  {
    vendorId: 1018,
    name: "Margaret Anderson",
    email: "margaret.a@example.com",
    mobileNumber: "+1 901-234-5678"
  },
  {
    vendorId: 1019,
    name: "Thomas Thomas",
    email: "thomas.t@example.com",
    mobileNumber: "+1 012-345-6789"
  },
  {
    vendorId: 1020,
    name: "Carol Jackson",
    email: "carol.j@example.com",
    mobileNumber: "+1 123-456-7890"
  }
];

const Employee = () => {
  const [employees, setEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editEmployeeId, setEditEmployeeId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [loading, setLoading] = useState(false);
  const [apiLoading, setApiLoading] = useState(false);
  const [error, setError] = useState("");
  const [usingDummyData, setUsingDummyData] = useState(true);
  const { showLoader, hideLoader } = useLoader();

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobileNumber: ""
  });

  // Initialize with dummy data on component mount
  useEffect(() => {
    setEmployees(dummyEmployees);
    setUsingDummyData(true);
  }, []);

  // Show snackbar message
  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  // Handle snackbar close
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle search
  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  // Handle add button click
  const handleAddClick = () => {
    setFormData({ name: "", email: "", mobileNumber: "" });
    setIsEditing(false);
    setEditEmployeeId(null);
    setOpenDialog(true);
  };

  // Handle edit button click
  const handleEditClick = (employee) => {
    setFormData({
      name: employee.name || "",
      email: employee.email || "",
      mobileNumber: employee.mobileNumber || ""
    });
    setIsEditing(true);
    setEditEmployeeId(employee.vendorId);
    setOpenDialog(true);
  };

  // Handle delete
  const handleDelete = async (vendorId) => {
    if (!vendorId) return;
    
    if (!window.confirm("Are you sure you want to delete this employee?")) return;

    setEmployees(employees.filter(emp => emp.vendorId !== vendorId));
    showSnackbar("Employee deleted successfully", "success");
  };

  // Handle form submit (Add/Update)
  const handleSubmit = async () => {
    // Validation
    if (!formData.name || !formData.email || !formData.mobileNumber) {
      showSnackbar("Please fill all fields", "error");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      showSnackbar("Please enter a valid email address", "error");
      return;
    }

    // Mobile validation
    if (formData.mobileNumber.length < 10) {
      showSnackbar("Please enter a valid mobile number", "error");
      return;
    }

    if (isEditing) {
      // Update existing employee
      setEmployees(employees.map(emp => 
        emp.vendorId === editEmployeeId 
          ? { ...emp, ...formData }
          : emp
      ));
      showSnackbar("Employee updated successfully!", "success");
    } else {
      // Add new employee
      const newId = Math.max(...employees.map(e => e.vendorId), 1000) + 1;
      const newEmployee = {
        ...formData,
        vendorId: newId
      };
      setEmployees([...employees, newEmployee]);
      showSnackbar("Employee added successfully!", "success");
    }
    
    setOpenDialog(false);
  };

  // Filter employees based on search
  const filteredEmployees = employees.filter(emp =>
    emp.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.mobileNumber?.includes(searchTerm)
  );

  return (
    <Box sx={{ 
  // height: "calc(100vh - 60px)",
  display: "flex",
  flexDirection: "column",

  overflow: "hidden",
  bgcolor: "#f5f5f5"
}}>
   
      {/* Fixed Header Section - No Scroll */}
      <FixedHeader >
        <Box sx={{ px: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <SearchField
                placeholder="Search by name, email, phone..."
                value={searchTerm}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#e66d33" }} />
                    </InputAdornment>
                  )
                }}
              />
              <Chip 
                label={`${filteredEmployees.length} Records`}
                size="small" 
                sx={{ 
                  bgcolor: "#e66d33", 
                  color: "#fff",
                  fontWeight: 500 
                }} 
              />
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddClick}
              sx={{
                backgroundColor: "#e66d33",
                "&:hover": { backgroundColor: "#9b4bc0" },
                borderRadius: "8px",
                textTransform: "none",
                px: 3
              }}
            >
              Add Employee
            </Button>
          </Box>
        </Box>
      </FixedHeader>

      {/* Scrollable Table Section - Only this scrolls */}
      <Box sx={{ 
        flex: 1, 
        px: 3,
        overflow: "hidden" // Important: prevents container scroll
      }}>
        <StyledTableContainer component={Paper}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <StyledTableHeadCell>S.NO</StyledTableHeadCell>
                <StyledTableHeadCell>Employee ID</StyledTableHeadCell>
                <StyledTableHeadCell>Employee Name</StyledTableHeadCell>
                <StyledTableHeadCell>Email</StyledTableHeadCell>
                <StyledTableHeadCell>Mobile Number</StyledTableHeadCell>
                <StyledTableHeadCell align="center">Actions</StyledTableHeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading || apiLoading ? (
                <TableRow>
                  <StyledTableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={40} />
                  </StyledTableCell>
                </TableRow>
              ) : filteredEmployees.length > 0 ? (
                filteredEmployees.map((emp, index) => (
                  <TableRow key={emp.vendorId || index} hover>
                    <StyledTableCell>{index + 1}</StyledTableCell>
                    <StyledTableCell>EMP{emp.vendorId}</StyledTableCell>
                    <StyledTableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: "#e66d33" }}>
                          <PersonIcon sx={{ fontSize: 18 }} />
                        </Avatar>
                        {emp.name}
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell>{emp.email}</StyledTableCell>
                    <StyledTableCell>{emp.mobileNumber}</StyledTableCell>
                    <StyledTableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleEditClick(emp)}
                        sx={{ color: "#e66d33", mr: 1 }}
                        title="Edit"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(emp.vendorId)}
                        sx={{ color: "#dc3545" }}
                        title="Delete"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </StyledTableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <StyledTableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    {error ? `Error: ${error}` : "No employees found"}
                  </StyledTableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </StyledTableContainer>
      </Box>

      {/* Add/Edit Dialog */}
      <StyledDialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <StyledDialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6">
              {isEditing ? "Edit Employee" : "Add New Employee"}
            </Typography>
            <IconButton size="small" onClick={() => setOpenDialog(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </StyledDialogTitle>

        <StyledDialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <TextField
              fullWidth
              label="Full Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              placeholder="Enter full name"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon sx={{ color: "#e66d33", fontSize: 20 }} />
                  </InputAdornment>
                )
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": { borderColor: "#e66d33" }
                }
              }}
            />

            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              placeholder="Enter email address"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon sx={{ color: "#e66d33", fontSize: 20 }} />
                  </InputAdornment>
                )
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": { borderColor: "#e66d33" }
                }
              }}
            />

            <TextField
              fullWidth
              label="Mobile Number"
              name="mobileNumber"
              value={formData.mobileNumber}
              onChange={handleInputChange}
              required
              placeholder="Enter mobile number"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon sx={{ color: "#e66d33", fontSize: 20 }} />
                  </InputAdornment>
                )
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": { borderColor: "#e66d33" }
                }
              }}
            />

            <Alert severity="info" sx={{ mt: 1 }}>
              You are using sample data. Changes will be saved locally.
            </Alert>
          </Box>
        </StyledDialogContent>

        <StyledDialogActions>
          <Button
            onClick={() => setOpenDialog(false)}
            startIcon={<CancelIcon />}
            sx={{ color: "#666" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            startIcon={<SaveIcon />}
            sx={{
              backgroundColor: "#e66d33",
              "&:hover": { backgroundColor: "#9b4bc0" }
            }}
          >
            {isEditing ? "Update" : "Save"}
          </Button>
        </StyledDialogActions>
      </StyledDialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert 
          severity={snackbar.severity}
          onClose={handleCloseSnackbar}
          sx={{ 
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Employee;